import { useRef } from 'react';
import cn from 'classnames';
import { twMerge } from 'tailwind-merge';
import { Markdown } from '@scale/scaleui';
import {
  useChatLayoutStore,
  ChatLayout,
} from '../../../ThreadedInteraction/hooks/useChatLayoutStore';
import { IndexedInteractionStepResponse } from '@scale/shared/types/chat';
import { StepCard } from '../../../ThreadedInteraction/step/StepCard';
import { usePinStore } from '../../../ThreadedInteraction/hooks/usePinStore';

interface Props {
  multiChatIndex: number;
  className?: string;
  responseIndex: number;
  response: IndexedInteractionStepResponse;
  isPinnedView?: boolean;
}

// eslint-disable-next-line complexity
export function PinnedResponse({
  multiChatIndex,
  className,
  responseIndex,
  response,
  isPinnedView = false,
}: Props) {
  const stepRef = useRef<HTMLDivElement>(null);
  const isDrawerOpen = useChatLayoutStore(state => state.isDrawerOpen);
  const setIsDrawerOpen = useChatLayoutStore(state => state.setIsDrawerOpen);
  const layout = useChatLayoutStore(state => state.layout);

  const isPinned = usePinStore(state => state.isPinnedIndex(responseIndex, multiChatIndex));
  const isPinnable = usePinStore(state => state.isPinnable(responseIndex));
  const addPinnedIndex = usePinStore(state => state.addPinnedIndex);
  const removePinnedIndex = usePinStore(state => state.removePinnedIndex);
  const showPinAction = isPinnable && layout === ChatLayout.Inline;

  const responseText = response.output;

  return (
    <>
      <StepCard className={twMerge(cn('focus:ring-0', className))}>
        <StepCard.Header>
          <StepCard.HeaderLeft>
            <StepCard.Image src="https://static.remotasks.com/uploads/602b25a6e0984c00343d3b26/Frame.png" />
            <StepCard.HeaderColumn>
              <StepCard.Title className="inline">
                Chat #{multiChatIndex + 1}
                {' - Response'}
              </StepCard.Title>
            </StepCard.HeaderColumn>
          </StepCard.HeaderLeft>

          <StepCard.HeaderRight>
            <StepCard.Button
              tooltip="Copy this response"
              icon="copy"
              onClick={e => {
                e.stopPropagation();
                navigator.clipboard.writeText(responseText);
              }}
            />
            <StepCard.Button
              icon={isPinned ? 'slash' : 'thumbtack'}
              iconProps={{
                mask: isPinned ? 'thumbtack' : undefined,
                transform: isPinned ? 'down-1' : undefined,
              }}
              disabled={!showPinAction}
              tooltip={
                !showPinAction
                  ? 'Open the pinned item drawer to pin this step'
                  : isPinned
                  ? 'Unpin this prompt'
                  : 'Pin this prompt'
              }
              onClick={() => {
                if (!isPinnable) return;
                if (!isDrawerOpen) setIsDrawerOpen(true);
                if (isPinned) {
                  removePinnedIndex(responseIndex, multiChatIndex);
                  return;
                }
                addPinnedIndex(responseIndex, multiChatIndex);
              }}
              enableDisabledTooltip
            />
          </StepCard.HeaderRight>
        </StepCard.Header>
        <StepCard.Body>
          <div
            className={cn('relative border-transparent', {
              'border rounded-md transition-border duration-150': isDrawerOpen && !isPinnedView,
              'w-full': isPinnedView,
            })}
            id={`${multiChatIndex}-step-${responseIndex}`}
            ref={stepRef}
          >
            <Markdown markdown={responseText}></Markdown>
          </div>
        </StepCard.Body>
      </StepCard>
    </>
  );
}
